import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Typography,
  TextField,
  Autocomplete,
} from "@mui/material";
import { MdOutlineFileUpload } from "react-icons/md";
import styles from "../features/Inventory/Inventory.module.css"; // Check this path based on your project structure
import client from "../services/client"; // Assuming your API client
import { BASE_URL, ENDPOINTS } from "../config"; // Assuming your API endpoints
import CommonSnackbar from "../components/commonComponents/Snackbar/CommonSnackbar";
import { readFile } from "./exportUtils"; // Assuming exportUtils has readFile
import ExcelReadTable from "../features/Inventory/ExcelReadTable"; // Check this path based on your project structure
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Spinner } from "../components/commonComponents/Spinner/Spinner";

const UploadInventoryModal = ({
  open,
  setOpen,
  alertOpen,
  setAlertOpen,
  message,
  setMessage,
  statusColor,
  setStatusColor,
  handleAlertClose,
  moduleHeaders,
  inventoryValidationRules, // This prop is now used by Mycomp too
  setLoading,
  loading,
  moduleName,
  fileSample,
  onUploadSuccess, // <--- NEW PROP ADDED HERE
  tableData,
  setTableData,
  fetchSimInventory,
}) => {
  const [fileExtension, setFileExtension] = useState({
    type: ".csv",
    name: "CSV",
  });
  const [providerList, setProviderList] = useState([]);
  const [provider, setProvider] = useState("");
  const [validatedData, setValidatedData] = useState([]);
  const [checkExcelError, setCheckExcelError] = useState(false);
  const [excelColumns, setExcelColumns] = useState([]);
  const [simType, setSimType] = useState([]);
  

  const navigate = useNavigate();

  useEffect(() => {
    // Only fetch providers if the modal is opened AND if we are likely in a context that needs it (e.g., ProfileInventory)
    // Mycomp doesn't use the provider directly for its local "upload"
    if (open && ENDPOINTS.getAllProvider) {
      // Check if endpoint exists to avoid errors in Mycomp
      fetchAllSimProvider();
    }
  }, [open]);

  const fetchAllSimProvider = async () => {
    setLoading(true);
    try {
      const { data, status, message } = await client.get(
        ENDPOINTS.getAllProvider
      );

      if (status === 200) {
        setProviderList(data);
        setStatusColor("success");
      } else {
        setStatusColor("error");
      }
      setMessage(message);
      setAlertOpen(true);
    } catch (err) {
      console.error("Error fetching providers:", err);
      setStatusColor("error");
      setMessage("Failed to fetch providers.");
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setValidatedData([]);
    setOpen(false);
    // Reset all form fields
    setSimType([]);
    setProvider("");
    setCheckExcelError(false);
    setMessage("");
    setStatusColor("");
    // Reset file input value to allow re-uploading the same file
    const fileInput = document.getElementById("file-upload");
    if (fileInput) {
      fileInput.value = "";
    }
  };

 

  const handleUploadInventory = async () => {
    // Check if SIM type is selected
    if (!simType || !simType.name) {
      setStatusColor("error");
      setMessage("Please select SIM Type.");
      setAlertOpen(true);
      return;
    }

    // Get file from input
    const fileInput = document.getElementById("file-upload");
    if (!fileInput || !fileInput.files[0]) {
      setStatusColor("error");
      setMessage("Please select a file to upload.");
      setAlertOpen(true);
      return;
    }

    const file = fileInput.files[0];

    // Prepare form data
    const formData = new FormData();
    formData.append("file", file); // backend expects 'file'
    formData.append("simType", simType?.name);
    // if (provider?.id) formData.append("vendorId", provider.id); // optional

    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      const res = await fetch(
        `${BASE_URL}sim/inventory/upload`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`, // ✅ attach token
          },
          body: formData, // FormData will handle multipart
        }
      );

      if (!res.ok) {
        let errorData = await res.json();
        let message = errorData.message || "Something Went Wrong";

        toast.error(message);
        throw new Error(`Error: ${res.status}`);
      }
      const data = await res.json();
      toast.success("File uploaded successfully!");

      fetchSimInventory();

      console.log("Response from backend:", data);
      setStatusColor("success");
      setMessage("File uploaded successfully!");
      setAlertOpen(true);
      handleClose(); // close modal after upload
    } catch (error) {
      console.error("Error uploading inventory:", error);
      setStatusColor("error");
      setMessage("Failed to upload file.");
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const fileInputHandler = (event) => {
    const file = event.target.files[0];
    if (!file) return; // Exit if no file selected
    const fileType = file.name.split(".").pop();

    // Clear previous data/errors on new file selection
    setValidatedData([]);
    setCheckExcelError(false);
    setMessage("");
    setStatusColor("");

    handleFileUpload(file, `.${fileType}`);
  };

  const handleFileUpload = async (file, fileType) => {
    setLoading(true)
    try {
      const data = await readFile(file, fileType);
      
      // Debug the raw data
      console.log("Raw data from file:", data.slice(0, 3));

      // Check if the uploaded file has the expected headers for the new format
      if (data.length > 0) {
        const fileHeaders = Object.keys(data[0]);
        console.log("File headers:", fileHeaders);
        
        const expectedHeaders = [
          // "S.no",
          "ICCID1",
          "IMSI1",
          "MSISDN1",
          "VENDOR1",

        ];

        // Very flexible header matching - normalize headers for comparison
        const normalizeForComparison = (header) => 
          header.toLowerCase()
                .replace(/\s+/g, '')  // Remove all whitespace
                .replace(/[_\-]/g, '') // Remove underscores and hyphens
                .replace(/iccid/i, 'iccid')
                .replace(/imsi/i, 'imsi')
                .replace(/msisdn/i, 'msisdn')
                .replace(/[^a-z0-9]/g, ''); // Remove any other special characters
        
        const normalizedFileHeaders = fileHeaders.map(h => normalizeForComparison(h));
        
        // Check if we have all required headers (more flexible matching)
        const missingHeaders = expectedHeaders.filter(
          (header) => {
            const normalizedHeader = normalizeForComparison(header);
            return !normalizedFileHeaders.some(
              (fileHeader) => fileHeader.includes(normalizedHeader)
            );
          }
        );

        console.log("Normalized file headers:", normalizedFileHeaders);
        
        if (missingHeaders.length > 0) {
          setStatusColor("error");
          setMessage(`Missing required columns: ${missingHeaders.join(", ")}`);
          setAlertOpen(true);
          setCheckExcelError(true);
          return;
        }
      }

      // Validate data using the rules passed from the parent (Mycomp or ProfileInventory)
      const result = validateData(data, inventoryValidationRules);

      // Extract headers from the first row of the processed data (which should contain all keys)
      // Use Object.keys from the first data item, and map them for ExcelReadTable
      const sanitizeColumns = (row) => {
  const cleaned = {};
  Object.keys(row || {}).forEach((key) => {
    if (
      key &&
      key.trim() !== "" &&
      !key.startsWith("__EMPTY") &&
      !key.toUpperCase().startsWith("EMPTY") &&
      key !== "error" &&
      key !== "message"
    ) {
      cleaned[key] = row[key];
    }
  });
  return cleaned;
};

const cleanedRows = result.map(sanitizeColumns);

const extractHeader =
  cleanedRows.length > 0 ? Object.keys(cleanedRows[0]) : [];


      setExcelColumns(
        extractHeader.map((item) => {
          return { key: item, label: item }; // Use original case for label for display
        })
      );
      setValidatedData(result);
      // Since we're skipping validation, there should be no errors
      setCheckExcelError(false);
      // Always show success message
      setStatusColor("success");
      setMessage("File data is valid. Click Upload to confirm.");
      setAlertOpen(true);
    } catch (error) {
      console.error("Error reading file:", error);
      setStatusColor("error");
      setMessage(`Failed to read file: ${error.message}`);
      setAlertOpen(true);
      setCheckExcelError(true);
    }finally{
      setLoading(false)
    }
  };

  const normalizeHeader = (header) => header.toLowerCase().replace(/\s+/g, "");

  const validateData = (data, validationRules) => {
    console.log("Validating data rows:", data.length);
    // Skip all validation except header match, which is done before this function
    
    return data.map((row, index) => {
      // Create a normalized version of the row just for consistency
      const normalizedRow = Object.keys(row).reduce((acc, key) => {
        // Clean up the value - trim spaces
        let value = (row[key] || "").toString().trim();
        // Remove any extra whitespace, including line breaks
        value = value.replace(/\s+/g, ' ').trim();
        
        // Normalize N/A values
        if (value.toUpperCase() === "N/A" || 
            value.toUpperCase() === "#N/A" || 
            value.toUpperCase() === "NA" || 
            value === "") {
          value = "N/A"; // Standardize all empty/N/A values
        }
        
        acc[normalizeHeader(key)] = value;
        return acc;
      }, {});

      // Skip all other validations as requested
      // No validation rules will be applied
      const errors = [];
      
      // Return row without any error flags
      return {
        ...row, // Keep original casing for display
        error: false, // Always set error to false
        message: "", // No error messages
      };

      // Prepare the result row: include original data, plus error status and message
      return {
        ...row, // Keep original casing for display in ExcelReadTable
        error: errors.length > 0,
        message: errors.join("; "), // Join multiple errors
      };
    });
  };


  return (
    <>
    {loading && <Spinner />}
   
    <Dialog
      open={open}
      className={styles.assignContainer}
      maxWidth={validatedData?.length > 0 ? "xxl" : "sm"}
      fullWidth
    >
      <DialogTitle className="modalThemeColor">
        <Typography variant="h6">{moduleName}</Typography>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Typography variant="h6" gutterBottom>
          {`${fileExtension?.name} Upload`}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              Upload Type <span style={{ color: "red" }}>*</span>
            </Typography>
            <Autocomplete
              size="small"
              options={[
                { type: ".csv", name: "CSV" },
                { type: ".xlsx", name: "Excel" },
              ]}
              getOptionLabel={(option) => option.name || ""}
              value={fileExtension}
              onChange={(e, newValue) => {
                setValidatedData([]);
                setCheckExcelError(false);
                setFileExtension(newValue);
                setMessage(""); // Clear messages on type change
                setStatusColor("");
                const fileInput = document.getElementById("file-upload");
                if (fileInput) {
                  fileInput.value = ""; // Clear file input
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select Upload Type"
                  variant="outlined"
                />
              )}
              style={{ marginBottom: 16 }}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              Select Sim Type <span style={{ color: "red" }}>*</span>
            </Typography>
            <Autocomplete
              size="small"
              value={simType || null} // null for no selection to show placeholder
              options={[{ name: "eSIM" }, { name: "Physical SIM" }]}
              getOptionLabel={(option) => option.name || ""}
              onChange={(e, newValue) => {
                setValidatedData([]);
                setCheckExcelError(false);
                setSimType(newValue);
                setMessage("");
                setStatusColor("");
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select SIM Type"
                  variant="outlined"
                />
              )}
              style={{ marginBottom: 16 }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle1" gutterBottom>
              File Selection:{" "}
              <a href={fileSample} download className={styles.downloadLink}>
                ({`Download Sample ${fileExtension?.name}`})
              </a>
            </Typography>
            <div className={styles.fileInputContainer}>
              <input
                type="file"
                disabled={!fileExtension?.type}
                accept={fileExtension?.type}
                id="file-upload"
                style={{ display: "none" }}
                onChange={fileInputHandler}
              />
              <label htmlFor="file-upload" className={styles.fileInputLabel}>
                <Typography
                  variant="body1"
                  component="span"
                  className={styles.fileInputText}
                >
                  {fileExtension?.type
                    ? "No File Chosen"
                    : "Please Select Upload Type"}
                </Typography>
              </label>
              <MdOutlineFileUpload className={styles.downloadIcon} />
            </div>
          </Grid>
          <ExcelReadTable
            validatedData={validatedData}
            excelColumns={excelColumns}
            fileExtension={fileExtension}
          />
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          color="primary"
          onClick={handleClose}
          variant="outlined"
          size="large"
        >
          Close
        </Button>
        <Button
          disabled={checkExcelError || validatedData.length === 0 || !simType} // Disable if provider not selected for API uploads or simType not selected
          color="success"
          onClick={handleUploadInventory}
          variant="contained"
          size="large"
          startIcon={loading && <Spinner size={20} />}
        >
          {loading ? "Uploading..." : "Upload"}
        </Button>
      </DialogActions>
      <CommonSnackbar
        open={alertOpen}
        message={message}
        handleClose={handleAlertClose}
        status={statusColor}
        setOpen={setAlertOpen} // Corrected setOpen to setAlertOpen for Snackbar
      />
    </Dialog>
     </>
  );
};

export default UploadInventoryModal;
